import { zodResolver } from '@hookform/resolvers/zod';
import {
  useForm as useRHForm,
  type FieldValues,
  type UseFormProps,
} from 'react-hook-form';

type TZodResolverFunc<TFieldValues extends FieldValues = FieldValues> =
  typeof zodResolver<TFieldValues>;

type TResolverParams<TFieldValues extends FieldValues = FieldValues> =
  Parameters<TZodResolverFunc<TFieldValues>>;

export interface IUseFormParams<
  TFieldValues extends FieldValues = FieldValues,
  TContext = TAny,
> extends Omit<UseFormProps<TFieldValues, TContext>, 'resolver'> {
  schema?: TResolverParams<TFieldValues>[0];
  schemaOptions?: TResolverParams<TFieldValues>[1];
  resolverOptions?: TResolverParams<TFieldValues>[2];
}

export function useForm<
  TFieldValues extends FieldValues = FieldValues,
  TContext = TAny,
  TTransformedValues extends FieldValues | undefined = undefined,
>({
  schema,
  schemaOptions,
  resolverOptions,
  ...rest
}: IUseFormParams<TFieldValues, TContext> = {}) {
  let resolver: ReturnType<TZodResolverFunc<TFieldValues>> | undefined;

  if (schema) {
    resolver = zodResolver(schema, schemaOptions, resolverOptions);
  }

  return useRHForm<TFieldValues, TContext, TTransformedValues>({
    resolver,
    mode: 'all',
    criteriaMode: 'all',
    reValidateMode: 'onChange',
    ...rest,
  });
}
